import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Edit } from "lucide-react";

export default function Branches() {
  const [items, setItems] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  function empty() { return { codigo: "", nombre: "", direccion: "", ciudad: "", provincia: "", telefono: "", responsable: "", establecimiento_sri: "001", punto_emision_sri: "001", es_matriz: false, activo: true }; }
  const load = async () => {
    const [b, bd] = await Promise.all([api.get("/branches"), api.get("/masters/catalog/bodegas")]);
    setItems(b.data); setBodegas(bd.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);
  const save = async () => {
    if (!form.codigo || !form.nombre) return toast.error("Código y nombre requeridos");
    try {
      if (editing) await api.patch(`/branches/${editing.id}`, form);
      else await api.post("/branches", form);
      toast.success("Sucursal guardada"); setOpen(false); setEditing(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`¿Eliminar sucursal ${s.codigo}?`)) return;
    try { await api.delete(`/branches/${s.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (s) => { setEditing(s); setForm({ ...empty(), ...s }); setOpen(true); };
  const asignarBodega = async (bodega_id, sucursal_id) => {
    try { await api.post("/branches/asignar-bodega", { bodega_id, sucursal_id: sucursal_id || null }); toast.success("Asignación actualizada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Sucursales</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Gestione múltiples sucursales con sus propios establecimientos/puntos de emisión SRI y asigne bodegas a cada una.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }} className="bg-[#205c45]" data-testid="new-branch-btn"><Plus size={16} className="mr-2" />Nueva sucursal</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(s => (
          <Card key={s.id} className="p-5 border border-[#d1dbd6]">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[#e9f2ee] grid place-items-center"><Building2 className="text-[#205c45]" size={18} /></div>
                <div>
                  <div className="font-semibold text-[#15241d]">{s.codigo}</div>
                  <div className="text-xs text-[#5c7368]">{s.nombre}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => edit(s)}><Edit size={14} /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(s)}><Trash2 size={14} /></Button>
              </div>
            </div>
            {s.es_matriz && <Badge className="bg-[#205c45] text-white mb-2">Matriz</Badge>}
            <div className="text-xs text-[#4a5c53] space-y-1">
              <div>{s.direccion || "—"}</div>
              <div>{s.ciudad}{s.provincia ? `, ${s.provincia}` : ""}</div>
              <div className="font-mono text-[11px]">SRI: {s.establecimiento_sri}-{s.punto_emision_sri}</div>
              {s.responsable && <div>Resp.: <strong>{s.responsable}</strong></div>}
            </div>
            <div className="mt-4 pt-3 border-t border-[#d1dbd6]">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#5c7368] mb-1">Bodegas asignadas</div>
              <div className="text-xs space-y-1">
                {bodegas.filter(b => b.sucursal_id === s.id).map(b => (
                  <div key={b.id} className="flex items-center justify-between gap-2">
                    <span>{b.nombre}</span>
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => asignarBodega(b.id, null)}>Quitar</Button>
                  </div>
                ))}
                {bodegas.filter(b => b.sucursal_id === s.id).length === 0 && <span className="text-[#8ba397]">Sin bodegas</span>}
              </div>
              {bodegas.filter(b => !b.sucursal_id).length > 0 && (
                <div className="mt-2">
                  <Select onValueChange={(v) => asignarBodega(v, s.id)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="+ Asignar bodega" /></SelectTrigger>
                    <SelectContent>{bodegas.filter(b => !b.sucursal_id).map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-3 text-center py-10 text-[#8ba397] bg-white rounded-md border border-[#d1dbd6]">Sin sucursales. Cree su matriz con código <strong>MAT</strong>.</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nueva"} sucursal</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="MAT / GYE01" /></div>
            <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
            <div className="col-span-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></div>
            <div><Label>Ciudad</Label><Input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></div>
            <div><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            <div><Label>Responsable</Label><Input value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} /></div>
            <div><Label>Establecimiento SRI</Label><Input value={form.establecimiento_sri} onChange={(e) => setForm({ ...form, establecimiento_sri: e.target.value })} placeholder="001" maxLength={3} /></div>
            <div><Label>Punto emisión SRI</Label><Input value={form.punto_emision_sri} onChange={(e) => setForm({ ...form, punto_emision_sri: e.target.value })} placeholder="001" maxLength={3} /></div>
            <div className="col-span-2 flex items-center gap-2"><Checkbox checked={form.es_matriz} onCheckedChange={(v) => setForm({ ...form, es_matriz: !!v })} /><Label>Es la sucursal matriz</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-branch-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
